import React from 'react';
import { Alert, Button, Card, Col, Empty, Row, Skeleton, Space, Statistic, Tag, Typography } from 'antd';
import { CloudOutlined, LineChartOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { MarketDayTypeCard, MarketDayTypeRegion } from '@/types/api';

interface MarketDayTypeCardsProps {
  cards: MarketDayTypeCard[];
  selectedDate?: string;
  loading?: boolean;
  error?: Error | null;
  onSelectDate: (date: string) => void;
  onRetry?: () => void;
}

function fmt(value: number | null | undefined, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toFixed(digits);
}

function pvImpactColor(level: string) {
  if (level === 'strong') return 'gold';
  if (level === 'weak') return 'blue';
  if (level === 'unknown') return 'default';
  return 'green';
}

function priceTagColor(label: string) {
  if (label.includes('负价') || label.includes('低价')) return 'red';
  if (label.includes('晚峰') || label.includes('峰谷')) return 'volcano';
  if (label.includes('平缓')) return 'green';
  return 'geekblue';
}

function RegionStrip({ regions }: { regions: MarketDayTypeRegion[] }) {
  if (!regions.length) return <Typography.Text type="secondary">关键区域暂无数据</Typography.Text>;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {regions.slice(0, 5).map((region) => (
        <div
          key={region.region}
          style={{
            display: 'grid',
            gridTemplateColumns: '52px minmax(76px, 1fr) 74px 66px',
            gap: 8,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <Typography.Text strong>{region.region}</Typography.Text>
          <Typography.Text>{region.weather_label}</Typography.Text>
          <Typography.Text type="secondary">{fmt(region.midday_irradiance)} W/m2</Typography.Text>
          <Typography.Text type="secondary">{fmt(region.avg_cloud_cover)}% 云</Typography.Text>
        </div>
      ))}
    </div>
  );
}

const MarketDayTypeCards: React.FC<MarketDayTypeCardsProps> = ({
  cards,
  selectedDate,
  loading,
  error,
  onSelectDate,
  onRetry,
}) => {
  if (loading) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>日型判断卡片</Typography.Title>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="日型判断卡片加载失败"
        description={error.message}
        action={onRetry ? <Button size="small" onClick={onRetry}>重试</Button> : undefined}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!cards.length) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>日型判断卡片</Typography.Title>
        <Empty description="暂无日型判断数据" />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Space align="center" wrap style={{ marginBottom: 10 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>日型判断卡片</Typography.Title>
        <Tag color="blue">2026-06-08 至 2026-06-14 默认周</Tag>
      </Space>
      <Row gutter={[12, 12]}>
        {cards.map((card) => {
          const active = selectedDate === card.date;
          return (
            <Col xs={24} md={12} xl={8} key={card.date}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  borderColor: active ? '#2563eb' : '#e5e7eb',
                  boxShadow: active ? '0 6px 18px rgba(37, 99, 235, 0.12)' : 'none',
                }}
                title={
                  <Space wrap>
                    <Button
                      type={active ? 'primary' : 'default'}
                      size="small"
                      onClick={() => onSelectDate(card.date)}
                    >
                      {card.date}
                    </Button>
                    <Tag color="cyan">{card.weather_summary.source_label}</Tag>
                  </Space>
                }
              >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <Typography.Text strong style={{ fontSize: 16 }}>{card.day_type_label}</Typography.Text>
                  <Space wrap size={[4, 4]}>
                    {card.price_shape.labels.map((label) => (
                      <Tag key={label} color={priceTagColor(label)} style={{ marginInlineEnd: 0 }}>
                        {label}
                      </Tag>
                    ))}
                    <Tag color={pvImpactColor(card.pv_impact.level)} style={{ marginInlineEnd: 0 }}>
                      {card.pv_impact.level === 'strong' ? '强光伏' : card.pv_impact.level === 'weak' ? '弱光伏' : '常规光伏'}
                    </Tag>
                  </Space>
                  <Row gutter={8}>
                    <Col span={8}>
                      <Statistic
                        title="午间辐照"
                        value={fmt(card.pv_impact.midday_irradiance)}
                        suffix="W/m2"
                        valueStyle={{ fontSize: 15 }}
                        prefix={<ThunderboltOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="云量"
                        value={fmt(card.pv_impact.midday_cloud_cover)}
                        suffix="%"
                        valueStyle={{ fontSize: 15 }}
                        prefix={<CloudOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="峰谷差"
                        value={fmt(card.price_shape.spread)}
                        suffix="元"
                        valueStyle={{ fontSize: 15 }}
                        prefix={<LineChartOutlined />}
                      />
                    </Col>
                  </Row>
                  <Typography.Text>{card.weather_summary.overview}</Typography.Text>
                  <Typography.Text>{card.pv_impact.judgement}</Typography.Text>
                  <Typography.Text type="secondary">{card.price_shape.summary}</Typography.Text>
                  <RegionStrip regions={card.weather_summary.key_regions} />
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default MarketDayTypeCards;
